import { fabric } from "fabric";
import { DoorType, InsideOptionType } from "../types/types";
import { DOOR_VIEW_COLOR, DOOR_BACKGROUND_IMAGE_COLOR } from "../types/Colors";
import { PATTERN_IMAGE, LEFT_DOOR_IMAGE, RIGHT_DOOR_IMAGE, UP_DOOR_IMAGE, DOWN_DOOR_IMAGE, DRAWER_DOOR_IMAGE, BOOTH_DOOR_IMAGE, IMAGE_INSIDE_OPTION_SPLIT, IMAGE_INSIDE_OPTION_RAIL, IMAGE_INSIDE_OPTION_DRAWER, IMAGE_INSIDE_OPTION_PANTOGRAPH, SLIDING_DOOR_IMAGE, } from "../types/Images";
/**
 * Render class
 * fabric js library wrapper
 */
export default class Render {
    constructor(options) {
        this.fontFamily = 'Arial';
        this._sizer = 1;
        this._width = 0;
        this._leftCenter = 0;
        this._topCenter = 0;
        this._fontSize = 10;
        this._isModelView = false;
        this._isDoorView = false;
        this._isInnerView = false;
        this._canvas = new fabric.StaticCanvas(options.canvas, {
            renderOnAddRemove: false,
        });
        this.isModelView = options.modelView !== undefined ? options.modelView : false;
        this.isDoorView = options.doorView !== undefined ? options.doorView : false;
        this.isInnerView = options.innerView !== undefined ? options.innerView : false;
    }
    get sizer() {
        return this._sizer;
    }
    set sizer(sizer) {
        this._sizer = sizer;
    }
    set isModelView(isModelView) {
        this._isModelView = isModelView;
    }
    set isDoorView(isDoorView) {
        this._isDoorView = isDoorView;
    }
    set isInnerView(isInnerView) {
        this._isInnerView = isInnerView;
    }
    set leftCenter(leftCenter) {
        this._leftCenter = leftCenter;
    }
    set topCenter(topCenter) {
        this._topCenter = topCenter;
    }
    set fontSize(fontSize) {
        this._fontSize = 14;
        this._fontSize = fontSize;
    }
    set width(width) {
        this._width = width;
    }
    clear() {
        this._canvas.clear();
    }
    renderAll() {
        this._canvas.renderAll();
    }
    setCanvasSize(width, height) {
        this._canvas.setWidth(width);
        this._canvas.setHeight(height);
        this._canvas.calcOffset();
    }
    setCanvas(canvas) {
        this._canvas.dispose();
        this._canvas = new fabric.StaticCanvas(canvas, {
            renderOnAddRemove: false,
        });
    }
    rectangle(rectangle, pattern = true, strokeWidth = 1, useDefaultColorForDoorView = true, backgroundColor = 'white', fillBackground = true) {
        let defaultColor = 'black';
        if (this._isDoorView && useDefaultColorForDoorView) {
            defaultColor = DOOR_VIEW_COLOR;
        }
        const r = new fabric.Rect({
            fill: fillBackground ? backgroundColor : null,
            stroke: defaultColor,
            strokeWidth: strokeWidth,
            top: (this._scale(rectangle.top) + this._topCenter),
            left: (this._scale(rectangle.left) + this._leftCenter),
            width: this._scale(rectangle.width),
            height: this._scale(rectangle.height),
            objectCaching: false,
            angle: rectangle.angle ? rectangle.angle : 0,
        });
        if (pattern) {
            r.fill = new fabric.Pattern({
                source: PATTERN_IMAGE,
                repeat: 'repeat'
            });
            this._canvas.add(r);
        }
        else {
            this._canvas.add(r);
        }
    }
    text(text) {
        const t = new fabric.Text(text.text.toString(), {
            top: this._scale(text.top) + this._topCenter,
            left: this._scale(text.left) + this._leftCenter,
            fontSize: text.fontSize ? text.fontSize : 14 * this.sizer,
            fontFamily: this.fontFamily,
            angle: text.angle,
            objectCaching: false,
            charSpacing: text.letterSpacing ? text.letterSpacing : 0
        });
        if (text.options !== undefined) {
            const { background, backgroundColor, textCentered } = text.options;
            const textSpacer = t.height * 0.15;
            if (text.isVertical) {
                if (t.angle > 0) {
                    t.top = t.top + (t.width / 2);
                    t.left = t.left - (t.height + textSpacer);
                }
                else {
                    t.top = t.top - (t.height / 2);
                    t.left = t.left - (t.width / 2);
                }
            }
            else if (!this._isModelView && !this._isDoorView) {
                t.top = textCentered
                    ? (t.top - (t.height / 2))
                    : t.top - (t.height + textSpacer);
                t.left = (t.left) - (t.width / 2);
            }
            if (background !== undefined && background) {
                t.backgroundColor = backgroundColor !== undefined ? backgroundColor : 'white';
            }
        }
        if (this._isModelView || this._isDoorView) {
            t.left = t.left - (t.width / 2);
        }
        if (text.options !== undefined && text.options.decrementOwnHeight !== undefined) {
            t.top = t.top - t.height;
        }
        if (text.options !== undefined && text.options.increaseByOwnHeight !== undefined) {
            t.top = t.top + (text.options.increaseByOwnHeight * t.height);
        }
        this._canvas.add(t);
    }
    line(line, strokeWidth = 1) {
        const l = new fabric.Line([
            this._scale(line.topX) + this._leftCenter,
            this._scale(line.topY) + this._topCenter,
            this._scale(line.bottomX) + this._leftCenter,
            this._scale(line.bottomY) + this._topCenter
        ], {
            fill: 'black',
            stroke: 'black',
            strokeWidth: strokeWidth,
            selectable: false,
            evented: false,
            objectCaching: false
        });
        this._canvas.add(l);
    }
    polygon(polygon, pattern = true, strokeWidth = 1, flip = false, useDefaultColorForDoorView = true) {
        let defaultColor = 'black';
        if (this._isDoorView && useDefaultColorForDoorView) {
            defaultColor = DOOR_VIEW_COLOR;
        }
        const p = new fabric.Polyline(polygon.points.map(({ x, y }) => {
            return {
                x: this._scale(x),
                y: this._scale(y),
            };
        }), {
            top: (this._scale(polygon.top) + this._topCenter),
            left: (this._scale(polygon.left) + this._leftCenter),
            fill: polygon.backgroundColor ? polygon.backgroundColor : 'white',
            stroke: defaultColor,
            strokeWidth: strokeWidth,
            objectCaching: false,
        });
        if (polygon.options !== undefined) {
            p.setOptions(polygon.options);
        }
        p.flipX = flip;
        if (pattern) {
            p.fill = new fabric.Pattern({
                source: PATTERN_IMAGE,
                repeat: 'repeat'
            });
            this._canvas.add(p);
        }
        else {
            this._canvas.add(p);
        }
    }
    circle(circle, pattern = false) {
        const c = new fabric.Circle({
            top: this._scale(circle.top) + this._topCenter,
            left: this._scale(circle.left) + this._leftCenter,
            radius: this._scale(circle.radius),
            stroke: 'black',
            strokeWidth: 1,
            fill: 'white',
            startAngle: circle.startAngle ? circle.startAngle : 0,
            endAngle: circle.endAngle ? circle.endAngle : Math.PI,
        });
        if (pattern) {
            c.fill = new fabric.Pattern({
                source: PATTERN_IMAGE,
                repeat: 'repeat'
            });
        }
        this._canvas.add(c);
    }
    // Render door image
    doorImage(image) {
        const img = new fabric.Image(this._getDoorImageByType(image.type), {
            top: this._scale(image.top) + this._topCenter,
            left: this._scale(image.left) + this._leftCenter,
            angle: image.angle,
            opacity: image.opacity,
            objectCaching: false,
            backgroundColor: DOOR_BACKGROUND_IMAGE_COLOR
        });
        img.scaleToWidth(this._scale(image.scale));
        this._canvas.add(img);
    }
    _getDoorImageByType(type) {
        if (type === DoorType.Left) {
            return LEFT_DOOR_IMAGE;
        }
        else if (type === DoorType.Right) {
            return RIGHT_DOOR_IMAGE;
        }
        else if (type === DoorType.Down) {
            return DOWN_DOOR_IMAGE;
        }
        else if (type === DoorType.Drawer) {
            return DRAWER_DOOR_IMAGE;
        }
        else if (type === DoorType.Both) {
            return BOOTH_DOOR_IMAGE;
        }
        else if (type === DoorType.Up) {
            return UP_DOOR_IMAGE;
        }
        else if (type === DoorType.Sliding) {
            return SLIDING_DOOR_IMAGE;
        }
    }
    // Render inside option icon image
    renderInsideOptionImage(insideImg) {
        // find image by type
        const image = !insideImg.doorImage
            ? this._getInsideOptionImageByType(insideImg.type)
            : this._getDoorImageByType(insideImg.type);
        if (image) {
            // top position
            let top = this._scale(insideImg.top - insideImg.scaleSize) + this._topCenter;
            if (insideImg.doorImage) {
                top = this._scale(insideImg.top) + this._topCenter;
            }
            // left position
            let left = this._scale(insideImg.left - insideImg.scaleSize) + this._leftCenter;
            if (insideImg.leftImage !== undefined && insideImg.leftImage) {
                left = this._scale(insideImg.left) + this._leftCenter;
            }
            const img = new fabric.Image(image, {
                top: top,
                left: left,
                angle: 0,
                opacity: 1,
                objectCaching: false,
                backgroundColor: DOOR_BACKGROUND_IMAGE_COLOR,
            });
            img.scaleToWidth(this._scale(insideImg.scaleSize));
            this._canvas.add(img);
        }
    }
    // Return image html element by inside option type
    _getInsideOptionImageByType(type) {
        if (type === InsideOptionType.Split) {
            return IMAGE_INSIDE_OPTION_SPLIT;
        }
        else if (type === InsideOptionType.Drawer) {
            return IMAGE_INSIDE_OPTION_DRAWER;
        }
        else if (type === InsideOptionType.Rail) {
            return IMAGE_INSIDE_OPTION_RAIL;
        }
        else if (type === InsideOptionType.Pantograph) {
            return IMAGE_INSIDE_OPTION_PANTOGRAPH;
        }
        return null;
    }
    _scale(s) {
        return s * this._sizer;
    }
}
//# sourceMappingURL=Render.js.map